<template>
  <Dialog v-model="dialogVisible" title="批量制码" width="560px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="120px"
    >
      <el-form-item label="权益类型" prop="rewardType">
        <el-radio-group v-model="formData.rewardType">
          <el-radio :value="1">优惠券</el-radio>
          <el-radio :value="2">积分</el-radio>
        </el-radio-group>
      </el-form-item>
      <el-form-item
        v-if="formData.rewardType === 1"
        label="优惠券模板"
        prop="couponTemplateId"
      >
        <el-select
          v-model="formData.couponTemplateId"
          class="!w-100%"
          filterable
          remote
          clearable
          placeholder="须为「指定发放」类型模板"
          :remote-method="searchTemplates"
          :loading="tplLoading"
        >
          <el-option
            v-for="item in templateOptions"
            :key="item.id"
            :label="`${item.name} (#${item.id})`"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item v-if="formData.rewardType === 2" label="积分面值" prop="rewardValue">
        <el-input-number v-model="formData.rewardValue" :min="1" :max="99999999" class="!w-100%" />
      </el-form-item>
      <el-form-item label="生成数量" prop="generateCount">
        <el-input-number v-model="formData.generateCount" :min="1" :max="500" class="!w-100%" />
      </el-form-item>
      <el-form-item label="批次号" prop="batchNo">
        <el-input v-model="formData.batchNo" clearable placeholder="可空，自动生成" />
      </el-form-item>
      <el-form-item label="生效时间" prop="validStartTime">
        <el-date-picker
          v-model="formData.validStartTime"
          class="!w-100%"
          type="datetime"
          value-format="YYYY-MM-DD HH:mm:ss"
          placeholder="生效时间"
        />
      </el-form-item>
      <el-form-item label="失效时间" prop="validEndTime">
        <el-date-picker
          v-model="formData.validEndTime"
          class="!w-100%"
          type="datetime"
          value-format="YYYY-MM-DD HH:mm:ss"
          placeholder="失效时间"
        />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="formData.remark" type="textarea" :rows="2" />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submit">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { ExchangeCodeApi } from '@/api/mall/promotion/exchangeCode'
import { getCouponTemplatePage } from '@/api/mall/promotion/coupon/couponTemplate'
import { CouponTemplateTakeTypeEnum } from '@/utils/constants'

defineOptions({ name: 'ExchangeCodeCreateForm' })

const message = useMessage()
const emit = defineEmits(['success'])

const dialogVisible = ref(false)
const formLoading = ref(false)
const formRef = ref()
const tplLoading = ref(false)
const templateOptions = ref<any[]>([])

const formData = ref({
  rewardType: 2,
  rewardValue: 100,
  couponTemplateId: undefined as number | undefined,
  generateCount: 10,
  batchNo: undefined as string | undefined,
  validStartTime: undefined as string | undefined,
  validEndTime: undefined as string | undefined,
  remark: undefined as string | undefined
})

const formRules = reactive({
  rewardType: [{ required: true, message: '请选择权益类型', trigger: 'change' }],
  generateCount: [{ required: true, message: '请输入数量', trigger: 'blur' }],
  validStartTime: [{ required: true, message: '请选择生效时间', trigger: 'change' }],
  validEndTime: [{ required: true, message: '请选择失效时间', trigger: 'change' }],
  couponTemplateId: [
    {
      validator: (_: any, val: any, cb: any) => {
        if (formData.value.rewardType === 1 && !val) cb(new Error('请选择优惠券模板'))
        else cb()
      },
      trigger: 'change'
    }
  ],
  rewardValue: [
    {
      validator: (_: any, val: any, cb: any) => {
        if (formData.value.rewardType === 2 && (!val || val <= 0)) cb(new Error('请填写积分面值'))
        else cb()
      },
      trigger: 'blur'
    }
  ]
})

const searchTemplates = async (keyword?: string) => {
  tplLoading.value = true
  try {
    // 兑券硬约束：仅「指定发放」模板（后端分页字段为 canTakeTypes，不是 takeType）
    const data = await getCouponTemplatePage({
      pageNo: 1,
      pageSize: 20,
      name: keyword || undefined,
      canTakeTypes: [CouponTemplateTakeTypeEnum.ADMIN.type]
    })
    templateOptions.value = data.list || []
  } finally {
    tplLoading.value = false
  }
}

const open = async () => {
  dialogVisible.value = true
  resetForm()
  const now = new Date()
  const end = new Date(now.getTime() + 30 * 24 * 3600 * 1000)
  const fmt = (d: Date) => {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
  }
  formData.value.validStartTime = fmt(now)
  formData.value.validEndTime = fmt(end)
  await searchTemplates()
}

const resetForm = () => {
  formData.value = {
    rewardType: 2,
    rewardValue: 100,
    couponTemplateId: undefined,
    generateCount: 10,
    batchNo: undefined,
    validStartTime: undefined,
    validEndTime: undefined,
    remark: undefined
  }
  formRef.value?.resetFields()
}

const submit = async () => {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    const batchNo = await ExchangeCodeApi.createBatch({ ...formData.value } as any)
    message.success(`制码成功，批次：${batchNo}`)
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

defineExpose({ open })
</script>
